// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

export type DnaSyngoviaFilterData = { active: boolean; label: string };
export type DnaSyngoviaDateRangeType = {
  display: string;
  range: { start: string; end: string };
  momentRange?: { start: moment.Moment; end: moment.Moment };
};

export type DnaSyngoviaScoreRangeType = {
  low: DnaSyngoviaFilterData;
  medium: DnaSyngoviaFilterData;
  high: DnaSyngoviaFilterData;
};

export type DnaSyngoviaResponsibleType = {
  customer: DnaSyngoviaFilterData;
  rsc: DnaSyngoviaFilterData;
  unknown: DnaSyngoviaFilterData;
};

export class DnaSyngoviaIssuesFilterModel {
  public dateRange: DnaSyngoviaDateRangeType = {
    display: '',
    range: { start: '', end: '' },
  };
  public responsible: DnaSyngoviaResponsibleType = {
    customer: { active: false, label: '' },
    rsc: { active: false, label: '' },
    unknown: { active: false, label: '' },
  };

  public scoreRange: DnaSyngoviaScoreRangeType = {
    low: { active: false, label: '' },
    medium: { active: false, label: '' },
    high: { active: false, label: '' },
  };

  public isScoreRangeActive<T extends keyof DnaSyngoviaScoreRangeType>(
    value: T,
  ): boolean {
    return this.scoreRange[value].active;
  }

  public isResponsibleActive<T extends keyof DnaSyngoviaResponsibleType>(
    value: T,
  ): boolean {
    return this.responsible[value].active;
  }

  public filterDateRange(identifiedAt: string): boolean {
    let result = true;
    const { start, end } = this.dateRange.range;
    if (!!start && !!end) {
      result =
        `${start}T00:00:00` <= identifiedAt &&
        identifiedAt <= `${end}T23:59:59`;
    }

    return result;
  }

  public filterScoreRange(scoreSeverity: string): boolean {
    let result = true;
    const filter: (keyof DnaSyngoviaScoreRangeType)[] = [];
    Object.keys(this.scoreRange).forEach((key) => {
      const property = key as keyof DnaSyngoviaScoreRangeType;
      if (this.scoreRange[property].active) {
        filter.push(property);
      }
    });

    if (
      filter.length > 0 &&
      !filter.includes(scoreSeverity as keyof DnaSyngoviaScoreRangeType)
    ) {
      result = false;
    }

    return result;
  }

  public filterResponsible(responsible: string): boolean {
    let result = true;
    const filter: (keyof DnaSyngoviaResponsibleType)[] = [];
    Object.keys(this.responsible).forEach((key) => {
      const property = key as keyof DnaSyngoviaResponsibleType;
      if (this.responsible[property].active) {
        filter.push(property);
      }
    });

    const resp = String(responsible).toLowerCase();
    if (
      filter.length > 0 &&
      !filter.includes(resp as keyof DnaSyngoviaResponsibleType)
    ) {
      result = false;
      if (filter.includes('unknown') && responsible === null) {
        result = true;
      }
    }

    return result;
  }

  public clearFilter(): void {
    this.clearDateRange();

    this.responsible = {
      customer: { active: false, label: '' },
      rsc: { active: false, label: '' },
      unknown: { active: false, label: '' },
    };

    this.scoreRange = {
      low: { active: false, label: '' },
      medium: { active: false, label: '' },
      high: { active: false, label: '' },
    };
  }

  public clearDateRange(): void {
    this.dateRange = {
      display: '',
      range: { start: '', end: '' },
    };
  }

  public clone(): DnaSyngoviaIssuesFilterModel {
    const cloned = new DnaSyngoviaIssuesFilterModel();

    cloned.dateRange.display = String(this.dateRange.display);
    cloned.dateRange.range.start = String(this.dateRange.range.start);
    cloned.dateRange.range.end = String(this.dateRange.range.end);
    if (this.dateRange.momentRange) {
      cloned.dateRange.momentRange = {
        start: this.dateRange.momentRange.start.clone(),
        end: this.dateRange.momentRange.end.clone(),
      };
    }

    for (const key of Object.keys(cloned.responsible)) {
      const property = key as keyof DnaSyngoviaResponsibleType;
      cloned.responsible[property] = { ...this.responsible[property] };
    }

    for (const key of Object.keys(cloned.scoreRange)) {
      const property = key as keyof DnaSyngoviaScoreRangeType;
      cloned.scoreRange[property] = { ...this.scoreRange[property] };
    }

    return cloned;
  }

  public merge(other: DnaSyngoviaIssuesFilterModel): void {
    this.dateRange.display = String(other.dateRange.display);
    this.dateRange.range.start = String(other.dateRange.range.start);
    this.dateRange.range.end = String(other.dateRange.range.end);
    if (other.dateRange.momentRange) {
      this.dateRange.momentRange = {
        start: other.dateRange.momentRange.start.clone(),
        end: other.dateRange.momentRange.end.clone(),
      };
    } else {
      this.dateRange.momentRange = undefined;
    }

    for (const key of Object.keys(this.responsible)) {
      const property = key as keyof DnaSyngoviaResponsibleType;
      this.responsible[property] = { ...other.responsible[property] };
    }

    for (const key of Object.keys(this.scoreRange)) {
      const property = key as keyof DnaSyngoviaScoreRangeType;
      this.scoreRange[property] = { ...other.scoreRange[property] };
    }
  }
}
